import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import API from '@sesame/web-api';
import { useAuth0 } from '@auth0/auth0-react';

import { ICustomer, ISalesorder, ISalesorderType, ILoadState } from 'src/types';
import Alert, { AlertTypes } from 'src/components/AlertDisplay';
import ModalDialog from 'src/components/ModalDialog';
import { getCustomerCacheList, getCustomerCacheMap } from 'src/util/resources/CustomerCache';
import { getSalesorderTypeCacheList } from 'src/util/resources/SalesorderTypesCache';

import { FormInput, FormSelect } from 'src/components/Form';
import { Spinner } from 'src/components/Spinner';


// Make a string based object to ease form manipulatin
type IFormData = { [Property in keyof ISalesorder]: string; } & { [key: string]: string };
type fieldNames = keyof IFormData;

type ICustomerMap = Map<number, string>;



interface IEditSalesorderDetailsProps {
  salesorderId?: number;
  onSave?: () => void;
}

export const EditSalesorderDetails = ({ salesorderId, onSave }: IEditSalesorderDetailsProps) => {
  return <ModalDialog
    id="newsalesorder"
    buttonText={salesorderId ? 'Edit' : 'New'}
    title="Add a Sales Order"
  >
    <EditSalesorderDetailForm
      salesorderId={salesorderId}
      onSave={onSave}
    />
  </ModalDialog>
}

export const EditSalesorderDetailForm = ({ salesorderId, onSave }: IEditSalesorderDetailsProps) => {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [customerMap, setCustomerMap] = useState<ICustomerMap>(new Map());
  const [salesorders, setSalesorders] = useState<ISalesorder[]>([]);
  const [salesorderTypes, setSalesorderTypes] = useState<string[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    salesorderId: '',
    salesorderRef: '',
    customerId: '',
    name: '',
    salesorderType: 'Sale',
    originalSalesOrder: '',
    salesOpportunity: '',
    issue: '',
  });
  const [validation, setValidation] = useState<fieldNames[]>([]);

  const validate = () => {
    const valid: fieldNames[] = [];
    const reqFields: fieldNames[] = ['salesorderRef', 'customerId', 'name', 'salesorderType', 'salesOpportunity', 'issue'];
    reqFields.forEach(d => { if (formData[d].trim() == '') { valid.push(d) } });

    if (formData.issue.match(/^[A-Z]+-\d+$/) == null && !valid.includes('issue')) {
      valid.push('issue');
    }
    setValidation(valid);
    return valid.length;
  }

  const save = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (validate() > 0) {
      return;
    }

    const newSalesorder: ISalesorder = {
      salesorderId: salesorderId || 0,
      salesorderRef: formData.salesorderRef,
      customerId: +formData.customerId,
      name: formData.name,
      salesorderType: formData.salesorderType,
      salesOpportunity: formData.salesOpportunity,
      issue: formData.issue
    };
    if (+(formData.originalSalesOrder || 0) > 0) {
      newSalesorder.originalSalesOrder = +(formData.originalSalesOrder || 0);
    }

    getAccessTokenSilently().then(accessToken => {
      if (salesorderId) {

        API.patch(accessToken, 'api/v1/salesorder', newSalesorder)
          .then((resp) => {
            Alert(AlertTypes.SUCCESS, 'Sales Order Updated');
            if (onSave) {
              onSave();
            }
            const el = document.getElementById('modal-close');
            if (el) {
              el.click()
            }
          }).catch((err) => {
            Alert(AlertTypes.ERROR, 'Error updating salesorder: ' + err.message);
          });
      } else {
        API.post(accessToken, 'api/v1/salesorder', newSalesorder)
          .then((resp) => {
            Alert(AlertTypes.SUCCESS, 'Sales Order Saved');
            if (onSave) {
              onSave();
            }
          }).catch((err) => {
            Alert(AlertTypes.ERROR, 'Error saving salesorder: ' + err.message);

          });
      }
    })
  }

  const formChange = (item: fieldNames, event: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newFormData = { ...formData };
    if (item == 'issue') {
      newFormData[item] = event.currentTarget.value.toUpperCase();
    } else {
      newFormData[item] = event.currentTarget.value;
    }

    setFormData(newFormData);
  }


  const getCustomerList = () => {
    getAccessTokenSilently().then(accessToken => {
      getCustomerCacheList(accessToken || '')
        .then((list) => {
          setCustomers(list);
          getCustomerCacheMap(accessToken || '')
            .then(custMap => setCustomerMap(custMap));
        });
    });
  }
  const getSalesorderTypes = () => {
    getAccessTokenSilently().then(accessToken => {
      getSalesorderTypeCacheList(accessToken || '')
        .then((list) => {
          setSalesorderTypes(list.map(t => t.name));
        });
    });
  }

  const getSalesorder = async (salesorderId: number): Promise<ISalesorder> => {
    const accessToken = await getAccessTokenSilently();
    const response = await API.get(accessToken, `api/v1/salesorder/${salesorderId}`)
      .catch((err) => {
        if (err.status === 404) {
          Alert(AlertTypes.ERROR, 'Salesorders Not Found');
          return;
        }
        console.log('Salesorders err: ' + JSON.stringify(err));
        Alert(AlertTypes.ERROR, 'Salesorders: ' + err.message);
      });
    const so = response.data as ISalesorder;
    return response.data;
}

const getSalesorders = () => {
  getAccessTokenSilently().then(accessToken => {

    API.get(accessToken, 'api/v1/salesorder')
      .then((response) => {
        if (!response) {
          Alert(AlertTypes.ERROR, 'Salesorders  Not Found');
          return;
        }
        const salesorders = (response.data as ISalesorder[])
          .filter(s => s.customerId == +formData.customerId && s.salesorderRef != formData.salesorderRef);
        setSalesorders(salesorders);
      })
      .catch((err) => {
        if (err.status === 404) {
          Alert(AlertTypes.ERROR, 'Salesorders Not Found');
          return;
        }
        console.log('Salesorders err: ' + JSON.stringify(err));
        Alert(AlertTypes.ERROR, 'Salesorders: ' + err.message);
      });
  })
}

useEffect(() => {
  getSalesorders();
}, [getAccessTokenSilently, formData.customerId]);

useEffect(() => {
  getCustomerList();
  getSalesorderTypes();
}, [getAccessTokenSilently ]);

useEffect(() => {
  if (salesorderId) {
    getSalesorder(salesorderId)
      .then(so => {
        if (so) {
          const newFormData: IFormData = {
            salesorderId: so.salesorderId + '',
            salesorderRef: so.salesorderRef,
            customerId: so.customerId + '',
            name: so.name,
            salesorderType: so.salesorderType,
            originalSalesOrder: so.originalSalesOrder + '',
            salesOpportunity: so.salesOpportunity,
            issue: so.issue,
          }
          setFormData(newFormData);
        }
      });
  } else {
    const newFormData: IFormData = {
      salesorderId: '',
      salesorderRef: '',
      customerId: '',
      name: '',
      salesorderType: 'Sale',
      originalSalesOrder: '',
      salesOpportunity: '',
      issue: '',
    }
    setFormData(newFormData);
  }
}, [salesorderId]);

if (isLoading) {
  return <Spinner />
}
return <form>
  <FormInput
    label="Sales Order Reference"
    name="salesorderRef"
    data={formData}
    changeFn={formChange}
    validation={validation}
  />

  <FormSelect
    label="Customer"
    name="customerId"
    data={formData}
    blankChoice='Select'
    displayFn={() => customers.map(c => { return { value: `${c.customerId}`, key: `${c.customerId}`, text: c.name } })}
    changeFn={formChange}
    validation={validation}
  />

  <FormInput
    label="Name"
    name="name"
    data={formData}
    changeFn={formChange}
    validation={validation}
  />

  <FormSelect
    label="Sales Order Type"
    name="salesorderType"
    data={formData}
    displayFn={() => salesorderTypes.map(t => { return { value: t, key: t, text: t } })}
    changeFn={formChange}
    validation={validation}
  />

  <FormSelect
    label="Original Sales Order (for RMAs)"
    name="originalSalesOrder"
    data={formData}
    blankChoice=''
    displayFn={() => salesorders.map(s => {
      return {
        value: `${s.salesorderId}`, key: `${s.salesorderId}`,
        text: `${s.salesorderRef} ` + (customerMap.has(s.customerId) ? ` - ${customerMap.get(s.customerId)}` : '')
      }
    }
    )}
    changeFn={formChange}
    validation={validation}
  />

  <FormInput
    label="Sales Opportunity"
    name="salesOpportunity"
    data={formData}
    changeFn={formChange}
    validation={validation}
  />

  <FormInput
    label="Ticket Tracking Number"
    name="issue"
    data={formData}
    changeFn={formChange}
    validation={validation}
  />

  <p className="uk-text-right">
    <button
      className="uk-button uk-button-primary uk-button-small"
      data-uk-toggle="newsalesorder"
      onClick={(e) => save(e)}>
      Save
    </button>
  </p>
</form>

}
